import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanStore } from '@/stores';
import type { PlanLimitError } from '@/types';
import { UpgradeModal } from './UpgradeModal';
import { trackUpgradeCta, type UpgradeTrigger } from '@/lib/upgradeTracking';

const TRIAL_ENDED_MESSAGE = 'Your free trial has ended. Upgrade to keep creating and publishing.';

/**
 * Opens the UpgradeModal for two distinct upgrade moments. Mount once, high in
 * the authenticated tree (AppLayout). Mirrors the `auth:unauthorized` →
 * useAuthRedirect convention.
 *
 *   • `billing:limit-reached` — httpClient 402 (PLAN_LIMIT_REACHED /
 *     FEATURE_NOT_IN_PLAN, LIL-341): user HAS a plan but hit a cap. CTA goes to
 *     the billing page to pick a higher tier.
 *   • `billing:feature-locked` — dispatched by the Sidebar (and other in-app
 *     entry points) when a NO-plan user clicks a premium feature. CTA goes to
 *     the full-screen Paywall (`/app/upgrade`) to subscribe.
 *
 *   • `billing:subscription-required` — 402 NO_ACTIVE_PLAN, handled ONLY in
 *     the lapsed-trial read-only shell, where reads never 402 so it means an
 *     attempted action. Elsewhere it can come from a background data call and
 *     must not pop a modal. CTA goes to the Paywall.
 */
export function UpgradeListener() {
  const navigate = useNavigate();
  const refreshUsage = usePlanStore(s => s.refreshUsage);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<PlanLimitError | null>(null);
  // Where the CTA should send the user — billing page (tier upgrade) vs the
  // full paywall (no plan). Defaults to billing.
  const [ctaTarget, setCtaTarget] = useState('/app/billing');
  const [cta, setCta] = useState<{ trigger: UpgradeTrigger; feature?: string }>({
    trigger: 'credits',
  });

  useEffect(() => {
    const onLimitReached = (e: Event) => {
      const detail = (e as CustomEvent).detail as PlanLimitError | undefined;
      setError(detail ?? null);
      setCtaTarget('/app/billing');
      setCta({ trigger: 'credits' });
      setOpen(true);
      // The limit was just hit — pull fresh usage so the sidebar meter/badge
      // reflect reality immediately rather than on the next page load.
      void refreshUsage();
    };

    const onFeatureLocked = (e: Event) => {
      const detail = (e as CustomEvent).detail as { feature?: string } | undefined;
      const feature = detail?.feature ?? 'This feature';
      setError({
        code: 'NO_ACTIVE_PLAN',
        message: `${feature} is a premium feature. Subscribe to unlock it and the rest of your LiLi workspace.`,
      } as PlanLimitError);
      setCtaTarget('/app/upgrade');
      setCta({ trigger: 'locked_feature', feature: detail?.feature });
      setOpen(true);
    };

    const onSubscriptionRequired = () => {
      if (!usePlanStore.getState().subscription?.readOnly) return;
      setError({
        code: 'NO_ACTIVE_PLAN',
        message: TRIAL_ENDED_MESSAGE,
      } as PlanLimitError);
      setCtaTarget('/app/upgrade');
      setCta({ trigger: 'trial_ending' });
      setOpen(true);
    };

    window.addEventListener('billing:limit-reached', onLimitReached);
    window.addEventListener('billing:feature-locked', onFeatureLocked);
    window.addEventListener('billing:subscription-required', onSubscriptionRequired);
    return () => {
      window.removeEventListener('billing:limit-reached', onLimitReached);
      window.removeEventListener('billing:feature-locked', onFeatureLocked);
      window.removeEventListener('billing:subscription-required', onSubscriptionRequired);
    };
  }, [refreshUsage]);

  const handleUpgrade = () => {
    trackUpgradeCta('upgrade_modal', cta.trigger, cta.feature);
    setOpen(false);
    navigate(ctaTarget);
  };

  return (
    <UpgradeModal open={open} onOpenChange={setOpen} error={error} onUpgrade={handleUpgrade} />
  );
}
